/**
 * @file 関数：共通関数
 * @license Copyright 2021 OPExPARK Inc.
 */

/**
 * 改行コード文字列を改行タグに変換
 * @param text {string} 文字列
 */
export const fnConvertLineStringToHtml = (text: string) => {
    let result = null;
    if (text !== null) {
        const temp1 = text.replace(/\n/g, "\n<br>\n").split("\n");
        const temp2 = temp1.map((t, index) => {
            if (t === "<br>") {
                return '<br key="br-' + index + '" />';
            } else {
                return t;
            }
        });
        result = temp2.join("");
    }
    return result;
};

/**
 * 秒を時間文字列に変換
 * @param second {number} 秒
 */
export const fnConvertSecondToTimeString = (second) => {
    const hours = fnZeroPadding(Math.floor(second / 3600), 2);
    const minutes = fnZeroPadding(Math.floor((second % 3600) / 60), 2);
    const seconds = fnZeroPadding(Math.floor(second % 60), 2);
    return hours + ":" + minutes + ":" + seconds;
};

/**
 * 時間文字列を秒に変換
 * @param timeString {string} 時間文字列
 */
export const fnConvertTimeStringToSecond = (timeString) => {
    const arrayTimeString = timeString.split(":");
    const hours = Number(arrayTimeString[0]);
    const minutes = Number(arrayTimeString[1]);
    const seconds = Number(arrayTimeString[2]);
    return hours * 3600 + minutes * 60 + seconds;
};

/**
 * 日時のフォーマット
 * @param date {object} Dateオブジェクト
 * @param formar {string} フォーマット文字列
 */
export const fnFormatDate = (date, format) => {
    format = format.replace(/yyyy/g, date.getFullYear());
    format = format.replace(/MM/g, ("0" + (date.getMonth() + 1)).slice(-2));
    format = format.replace(/dd/g, ("0" + date.getDate()).slice(-2));
    format = format.replace(/HH/g, ("0" + date.getHours()).slice(-2));
    format = format.replace(/mm/g, ("0" + date.getMinutes()).slice(-2));
    format = format.replace(/ss/g, ("0" + date.getSeconds()).slice(-2));
    format = format.replace(/SSS/g, ("00" + date.getMilliseconds()).slice(-3));
    return format;
};

/**
 * 特定のキーのcookieの値を取得
 * @param key {string} キー
 */
export const fnGetCookie = (key) => {
    const cookies = {};
    if (document.cookie) {
        const tempCookies = document.cookie.split("; ");
        tempCookies.forEach((cookie) => {
            const temp = cookie.split("=");
            cookies[temp[0]] = temp[1];
        });
    }
    return cookies[key];
};

/**
 * カルチャー言語を取得
 */
export const fnGetCultureLanguage = () => {
    // URLから言語判定する（SEO対策）
    const path = window.location.pathname;
    //console.log(`seo path:${path}`);
    if (path.startsWith("/ja/")) return "ja";
    if (path.startsWith("/en/")) return "en";
    if (path.startsWith("/surgeon/ja/")) return "ja";
    if (path.startsWith("/surgeon/en/")) return "en";

    // URLから言語判定できない場合はクッキーから判定する
    const cookie = fnGetCookie(".AspNetCore.Culture");
    if (cookie) {
        const languageCode = decodeURIComponent(cookie)
            .split("|")[1]
            .split("=")[1];
        return languageCode;
    } else {
        return "en";
    }
};

/**
 * 診療科コード取得
 */
export const fnGetDepartmentCode = () => {
    return window.dataPage !== undefined && window.dataPage.departmentCode
        ? window.dataPage.departmentCode
        : null;
};

/**
 * 診療科名取得
 */
export const fnGetDepartmentName = () => {
    return window.dataPage !== undefined && window.dataPage.departmentName
        ? window.dataPage.departmentName
        : null;
};

/**
 * 診療科パス名取得
 */
export const fnGetDepartmentPathname = () => {
    let result;
    let languageCodeOperation = fnGetCultureLanguage();
    switch (fnGetDepartmentCode()) {
        case 1:
            result = "/" + languageCodeOperation + "/neurosurgery";
            break;
        case 2:
            result = "/" + languageCodeOperation + "/gastroenterology";
            break;
        case 3:
            result = "/" + languageCodeOperation + "/reconstructivesurgery";
            break;
        default:
            result = "/" + languageCodeOperation + "/neurosurgery";
    }
    return result;
};

/**
 * パラメーター取得
 * @param key {string} 取得するキー
 */
export const fnGetParameter = (key) => {
    let search = window.location.search;
    if (search) {
        search = search.substring(1);
        const parameters = search.split("&");
        for (const index in parameters) {
            if (parameters[index].indexOf("=") !== -1) {
                const parameter = parameters[index].split("=");
                if (parameter[0] === key) {
                    return parameter[1];
                }
            } else {
                if (parameters[index] === key) {
                    return null;
                }
            }
        }
    } else {
        return null;
    }
};

/**
 * スポンサーコンテンツ取得APIのパラメーター文字列を正しいパラメーターの並び順で取得
 * @param addQueries {{}} 追加もしくは上書きするクエリオブジェクト
 */
const fnGetParametersApiAdMedias = (addQueries = {}) => {
    // APIパラメーターkey並び順で格納
    const searchKeys = [
        "tagId",
        "surgeonId",
        "partCode",
        "typeCode",
        "nameCode",
        "categoryCode",
        "keyword",
        "offset",
        "limit",
        "language",
    ];
    // APIパラメーターのvalueをURLパラメーターから取得し格納
    const searchValues = []; // value格納用
    searchKeys.forEach((key) => {
        let addValue;
        switch (key) {
            case "tagId":
                addValue = fnGetParameter("tag");
                break;
            case "surgeonId":
                if (/\/surgeon\/[0-9]{1,}/.test(location.pathname)) {
                    addValue = location.pathname.split("/")[3];
                } else {
                    addValue = null;
                }
                break;
            case "partCode":
                if (
                    window.dataSearch !== undefined &&
                    window.dataSearch.partCode !== 0
                ) {
                    addValue = window.dataSearch.partCode;
                } else {
                    addValue = null;
                }
                break;
            case "typeCode":
                if (
                    window.dataSearch !== undefined &&
                    window.dataSearch.typeCode !== 0
                ) {
                    addValue = window.dataSearch.typeCode;
                } else {
                    addValue = null;
                }
                break;
            case "nameCode":
                if (
                    window.dataSearch !== undefined &&
                    window.dataSearch.operationNameCode !== 0
                ) {
                    addValue = window.dataSearch.operationNameCode;
                } else {
                    addValue = null;
                }
                break;
            case "categoryCode":
                if (
                    /\/limited-edition\/[0-9]{1,}/.test(location.pathname) ||
                    /\/special\/[0-9]{1,}/.test(location.pathname)
                ) {
                    addValue = location.pathname.split("/").slice(-1)[0];
                } else {
                    addValue = null;
                }
                break;
            case "language":
                addValue = fnGetCultureLanguage();
                break;
            default:
                addValue = fnGetParameter(key);
        }
        const value =
            addQueries[key] !== undefined ? addQueries[key] : addValue;
        searchValues.push(value);
    });
    // keyとvalueをパラメーターストリングに変換
    const result = [];
    searchValues.forEach((value, index) => {
        if (value) {
            result.push(searchKeys[index] + "=" + value);
        }
    });
    // パラメーターを文字列で返す
    return result.length > 0 ? result.join("&") : "";
};

/**
 * プロモーションコンテンツ取得APIのパラメーター文字列を正しいパラメーターの並び順で取得
 * @param addQueries {{}} 追加もしくは上書きするクエリオブジェクト
 */
const fnGetParametersApiPromotions = (addQueries = {}) => {
    // APIパラメーターkey並び順で格納
    const searchKeys = [
        "tagId",
        "surgeonId",
        "partCode",
        "typeCode",
        "nameCode",
        "keyword",
        "offset",
        "limit",
        "language",
    ];
    // APIパラメーターのvalueをURLパラメーターから取得し格納
    const searchValues = []; // value格納用
    searchKeys.forEach((key) => {
        let addValue;
        switch (key) {
            case "tagId":
                addValue = fnGetParameter("tag");
                break;
            case "surgeonId":
                if (/\/surgeon\/[0-9]{1,}/.test(location.pathname)) {
                    addValue = location.pathname.split("/")[3];
                } else {
                    addValue = null;
                }
                break;
            case "partCode":
                if (
                    window.dataSearch !== undefined &&
                    window.dataSearch.partCode !== 0
                ) {
                    addValue = window.dataSearch.partCode;
                } else {
                    addValue = null;
                }
                break;
            case "typeCode":
                if (
                    window.dataSearch !== undefined &&
                    window.dataSearch.typeCode !== 0
                ) {
                    addValue = window.dataSearch.typeCode;
                } else {
                    addValue = null;
                }
                break;
            case "nameCode":
                if (
                    window.dataSearch !== undefined &&
                    window.dataSearch.operationNameCode !== 0
                ) {
                    addValue = window.dataSearch.operationNameCode;
                } else {
                    addValue = null;
                }
                break;
            case "language":
                addValue = fnGetCultureLanguage();
                break;
            default:
                addValue = fnGetParameter(key);
        }
        const value =
            addQueries[key] !== undefined ? addQueries[key] : addValue;
        searchValues.push(value);
    });
    // keyとvalueをパラメーターストリングに変換
    const result = [];
    searchValues.forEach((value, index) => {
        if (value) {
            result.push(searchKeys[index] + "=" + value);
        }
    });
    // パラメーターを文字列で返す
    return result.length > 0 ? result.join("&") : "";
};

/**
 * 症例検索用診療科コード取得
 */
export const fnGetPartCode = () => {
    return window.dataSearch !== undefined ? window.dataSearch.partCode : null;
};

/**
 * 執刀医名を言語ごとのフォーマットで取得
 * @param surgeon {{}} 執刀医データ
 * @param languageCode {string} 言語コード（省略時英語）
 */
export const fnGetSurgeonFullName = (
    surgeon: {
        firstName: string;
        lastName: string;
        middleName: string;
    },
    languageCode = "en"
) => {
    let surgeonName = "Dr. ";
    if (languageCode === "ja") {
        // 日本語の場合、姓（＋ミドルネーム）＋名の順
        surgeonName += surgeon.lastName + " ";
        surgeonName += surgeon.middleName ? surgeon.middleName + " " : "";
        surgeonName += surgeon.firstName;
    } else {
        // 日本語以外の場合、名（＋ミドルネーム）＋姓の順
        surgeonName += surgeon.firstName + " ";
        surgeonName += surgeon.middleName ? surgeon.middleName + " " : "";
        surgeonName += surgeon.lastName;
    }
    return surgeonName;
};

/**
 * 短縮表記の執刀医名を言語ごとのフォーマットで取得
 * @param surgeon {{}} 執刀医データ
 * @param languageCode {string} 言語コード（省略時英語）
 */
export const fnGetSurgeonShortenedName = (
    surgeon: {
        firstName: string;
        firstNameInitial: string;
        lastName: string;
        middleName: string;
    },
    languageCode = "en"
) => {
    let surgeonName = "";
    if (languageCode === "ja") {
        // 日本語の場合、姓（＋ミドルネーム）＋名の順
        surgeonName += surgeon.lastName + " ";
        surgeonName += surgeon.middleName ? surgeon.middleName + " " : "";
        surgeonName += surgeon.firstName;
    } else {
        // 日本語以外の場合、名（＋ミドルネーム）＋姓の順
        surgeonName += surgeon.firstNameInitial + ". ";
        surgeonName += surgeon.middleName ? surgeon.middleName + " " : "";
        surgeonName += surgeon.lastName;
    }
    return surgeonName;
};

/**
 * スポンサーユーザーの該当スポンサーコンテンツ取得APIのURL
 * @param addQueries {{}} 追加もしくは上書きするクエリオブジェクト
 */
export const fnGetUrlApiAdMediasSponsorUser = (addQueries = {}) => {
    // 症例カードプロモーション
    const url = "/api/promotions/sponser/" + fnGetDepartmentCode() + "/1";
    const params =
        addQueries !== null ? fnGetParametersApiAdMedias(addQueries) : "";
    return params !== "" ? url + "?" + params : url;
};

/**
 * 症例検索APIのURLを正しいパラメーターの並び順で取得
 * @param addQueries {{}} 追加もしくは上書きするクエリオブジェクト
 */
export const fnGetUrlApiOperations = (addQueries = {}) => {
    const searchKeys = [
        "tagId",
        "surgeonId",
        "partCode",
        "typeCode",
        "nameCode",
        "categoryCode",
        "keyword",
        "offset",
        "limit",
        "sorttype",
        "order",
        "language",
        "paidContentOnly",
        "bundleContentOnly",
        "bundleContentId",
    ];
    const searchValues = [];
    searchKeys.forEach((key) => {
        let addValue;
        switch (key) {
            case "tagId":
                addValue = fnGetParameter("tag");
                break;
            case "surgeonId":
                const match = location.pathname.match(/\/surgeon\/(\d+)/);
                addValue = match ? match[1] : null;
                break;
            case "partCode":
                if (
                    window.dataSearch !== undefined &&
                    window.dataSearch.partCode !== 0
                ) {
                    addValue = window.dataSearch.partCode;
                } else {
                    addValue = null;
                }
                break;
            case "typeCode":
                if (
                    window.dataSearch !== undefined &&
                    window.dataSearch.typeCode !== 0
                ) {
                    addValue = window.dataSearch.typeCode;
                } else {
                    addValue = null;
                }
                break;
            case "nameCode":
                if (
                    window.dataSearch !== undefined &&
                    window.dataSearch.operationNameCode !== 0
                ) {
                    addValue = window.dataSearch.operationNameCode;
                } else {
                    addValue = null;
                }
                break;
            case "categoryCode":
                if (
                    /\/limited-edition\/[0-9]{1,}/.test(location.pathname) ||
                    /\/special\/[0-9]{1,}/.test(location.pathname)
                ) {
                    addValue = location.pathname.split("/").slice(-1)[0];
                } else {
                    addValue = null;
                }
                break;
            case "sorttype":
                addValue = fnGetParameter("sort");
                break;
            case "language":
                addValue = fnGetCultureLanguage();
                break;
            case "paidContentOnly":
                addValue = null;
                break;
            case "bundleContentOnly":
                addValue = null;
                break;
            case "bundleContentId":
                if (/\/bundle\/[0-9]{1,}/.test(location.pathname)) {
                    addValue = location.pathname.split("/").slice(-1)[0];
                } else {
                    addValue = null;
                }
                break;
            default:
                addValue = fnGetParameter(key);
        }
        const value =
            addQueries[key] !== undefined ? addQueries[key] : addValue;
        searchValues.push(value);
    });
    const result = [];
    searchValues.forEach((value, index) => {
        if (value) {
            result.push(searchKeys[index] + "=" + value);
        }
    });
    // APIエンドポイント
    let url;
    if (addQueries["bundleContentOnly"] === true) {
        url = "/api/sales/product/bundle/operations/" + fnGetDepartmentCode();
    } else {
        url = "/api/operations/" + fnGetDepartmentCode();
    }
    return result.length > 0 ? url + "?" + result.join("&") : url;
};

/**
 * サムネイルリスト取得API
 * @param apiUrl {string} API（末尾「/」は含まないこと）
 * @param addQueries {{}} 追加もしくは上書きするクエリオブジェクト
 */
export const fnGetUrlApiThumbnaillist = (apiUrl, addQueries = {}) => {
    // APIパラメーターkey並び順で格納
    const searchKeys = ["order", "offset", "limit", "language"];
    // APIパラメーターのvalueをURLパラメーターから取得し格納
    const searchValues = []; // value格納用
    searchKeys.forEach((key) => {
        let addValue;
        switch (key) {
            case "language":
                addValue = fnGetCultureLanguage();
                break;
            default:
                addValue = fnGetParameter(key);
        }
        const value =
            addQueries[key] !== undefined ? addQueries[key] : addValue;
        searchValues.push(value);
    });
    const result = [];
    searchValues.forEach((value, index) => {
        if (value) {
            result.push(searchKeys[index] + "=" + value);
        }
    });
    const url = apiUrl + "/" + fnGetDepartmentCode();
    return result.length > 0 ? url + "?" + result.join("&") : url;
};

/**
 * 症例検索のURLを正しいパラメーターの並び順で取得
 * @param pathname {string} pathname
 * @param addQueries {{}} 追加もしくは上書きするクエリオブジェクト
 */
export const fnGetUrlOperations = (pathname?: string, addQueries = {}) => {
    let url;
    if (pathname !== null) {
        // pathnameが指定されている場合
        url = pathname;
    } else {
        // urlが指定されていない場合
        // カテゴリー（category）からデータ生成
        const categoryKeys = ["category"];
        const categoryValues = [];
        categoryKeys.forEach((key) => {
            const value =
                addQueries[key] !== undefined ? addQueries[key] : null;
            categoryValues.push(value);
        });
        // カテゴリー（category）が指定されている場合、location.pathnameからURLを生成
        if (categoryValues[0] !== null) {
            const array = location.pathname.split("/");
            array.pop();
            url = array.join("/") + "/" + categoryValues[0];
        } else {
            // カテゴリー（category）が指定されていない場合、location.pathnameから診療科部分の検索結果URLを指定
            url = fnGetDepartmentPathname();
            // 執刀医ID（surgeonId）、執刀医名（surgeonString）からデータ生成
            const surgeonKeys = ["surgeonId", "surgeonString"];
            const surgeonValues = [];
            surgeonKeys.forEach((key) => {
                const value =
                    addQueries[key] !== undefined ? addQueries[key] : null;
                surgeonValues.push(value);
            });
            // 執刀医ID（surgeonId）、執刀医名（surgeonString）が指定されている場合、執刀医検索URLを指定
            if (!surgeonValues.includes(null)) {
                url += "/surgeon/" + surgeonValues[0] + "/" + surgeonValues[1];
                // 執刀医ID（surgeonId）、執刀医名（surgeonString）が指定されていない場合、術式分類（type）、手術分類（name）をチェック
            } else {
                // 術式分類（type）、手術分類（name）からデータ生成
                const operationCategoryKeys = ["type", "name"];
                const operationCategoryValues = [];
                operationCategoryKeys.forEach((key) => {
                    const value =
                        addQueries[key] !== undefined ? addQueries[key] : null;
                    operationCategoryValues.push(value);
                });
                // 術式分類（type）が指定されている場合、術式分類URLを指定
                if (operationCategoryValues[0] !== null) {
                    url += "/" + operationCategoryValues[0];
                    // 手術分類（name）が指定されている場合、術式分類URLを指定
                    if (operationCategoryValues[1] !== null) {
                        url += "/" + operationCategoryValues[1];
                    }
                } else {
                    url += "/search";
                }
            }
        }
    }
    // 正しいパラメーターの並び順で配列化
    const paramsKeys = ["page", "order", "sort", "tag", "keyword"];
    const paramsValues = [];
    paramsKeys.forEach((key) => {
        const value =
            addQueries[key] !== undefined
                ? addQueries[key]
                : fnGetParameter(key);
        paramsValues.push(value);
    });
    const params = [];
    paramsValues.forEach((value, index) => {
        if (value) {
            params.push(paramsKeys[index] + "=" + value);
        }
    });
    // パラメーター配列がある場合、URLに追加
    if (params.length > 0) {
        url += "?" + params.join("&");
    }
    // URLを返す
    return url;
};

/**
 * プロモーションコンテンツ取得APIのURL
 * @param addQueries {{}} 追加もしくは上書きするクエリオブジェクト
 */
export const fnGetUrlApiPromotions = (addQueries = {}) => {
    // 症例カードプロモーション
    const url = "/api/promotions/" + fnGetDepartmentCode() + "/1";
    const params =
        addQueries !== null ? fnGetParametersApiPromotions(addQueries) : "";
    return params !== "" ? url + "?" + params : url;
};

/**
 * プロモーションコンテンツ取得APIのURL
 * @param addQueries {{}} 追加もしくは上書きするクエリオブジェクト
 */
export const fnGetUrlApiPromotionsSliderContent = (addQueries = {}) => {
    // スライダープロモーション
    const url = "/api/promotions/" + fnGetDepartmentCode() + "/2";
    const params =
        addQueries !== null ? fnGetParametersApiPromotions(addQueries) : "";
    return params !== "" ? url + "?" + params : url;
};


/**
 * フルバージョン動画ページのURLを正しいパラメーターの並び順で取得
 * @param addQueries {{}} 追加もしくは上書きするクエリオブジェクト
 */
export const fnGetUrlVideo = (addQueries = {}) => {
    const searchKeys = ["from", "tab", "playLogId", "opId", "cl"];
    const searchValues = [];
    searchKeys.forEach((key) => {
        const value =
            addQueries[key] !== undefined
                ? addQueries[key]
                : fnGetParameter(key);
        searchValues.push(value);
    });
    const result = [];
    searchValues.forEach((value, index) => {
        if (value) {
            result.push(searchKeys[index] + "=" + value);
        }
    });
    return result.length > 0
        ? location.pathname + "?" + result.join("&")
        : location.pathname;
};

/**
 * APIリクエストレスポンスエラー時処理
 */
export const fnHandleApiErrors = (response) => {
    // エラーページにリダイレクトされた場合
    if (response.url.indexOf("Error") !== -1) {
        throw new Error("An error has occurred.");
    }
    // ステータスコードが200番台以外の場合
    if (!response.ok) {
        throw new Error(String(response.status));
    }
    // エラーではない場合
    return response;
};

/**
 * APIリクエストレスポンス成功時処理
 */
export const fnHandleApiSuccess = (response) => {
    // OK
    if (response.status === 200) {
        return response.json();
    }
    // No Content
    if (response.status === 204) {
        return null;
    }
};

/**
 * Google Tag Manager カスタムイベントトリガー関数
 */
// データレイヤーにカスタムイベントをプッシュ
export const fnPushDataLayerGtm = (
    data:
        | DataLayerOperationCard
        | DataLayerNonOperationCard
        | DataLayerVideo
        | DataLayerVideoProgress
): void => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
};

/**
 * 代入式翻訳テキストの結果を返す
 * @param localizedText {string} 代入式翻訳テキスト
 * @param array {string[]} 置き換えテキストの配列
 */
export const fnReplaceLocalizedText = (localizedText, array) => {
    let tempLocalizedText = localizedText;
    array.forEach((text, index) => {
        const regexp = new RegExp("\\{" + index + "\\}", "g");
        tempLocalizedText = tempLocalizedText.replace(regexp, text);
    });
    return tempLocalizedText;
};

/**
 * cookieに保存する
 * @param key {string} キー
 * @param value {any} 値
 * @param expires {number} 保存日数
 * @param path {string} パス
 */
export const fnSetCookie = (key, value, expires = null, path = null) => {
    let result = key + "=" + value + "; ";
    if (expires) {
        const date = new Date();
        date.setDate(date.getDate() + expires);
        result += "expires=" + date.toUTCString() + "; ";
    }
    if (path) {
        result += "path=" + path + "; ";
    }
    document.cookie = result;
};

/**
 * モーダルを開閉時などにbody固定・解除する
 * @param flag {boolean} true：固定、false：解除
 */
export const fnSetFixedBody = (flag: boolean) => {
    const body = document.querySelector("body");
    if (flag === true) {
        const scrollY = window.scrollY;
        body.style.position = "fixed";
        body.style.top = -scrollY + "px";
        body.style.width = "100%";
    } else {
        const scrollY = -parseInt(body.style.top);
        body.style.position = null;
        body.style.top = null;
        body.style.width = null;
        if (!Number.isNaN(scrollY)) {
            window.scrollTo(0, scrollY);
        }
    }
};

/**
 * 0埋め
 * @param target {number} 対象
 * @param digit {number} 桁数
 */
export const fnZeroPadding = (target, digit) => {
    return ("00000000" + target).slice(-digit);
};

/**
 * URLパスの言語部分を変更する
 * @param path {string} urlパス
 * @param lang {string} 変更言語
 */
export const changeLanguagePath = (path: string, lang: string) => {
    // ① 先頭が /ja または /en の場合
    if (/^\/(ja|en)\//.test(path)) {
        return path.replace(/^\/(ja|en)\//, `/${lang}/`);
    }
    // ② パス途中に /ja/ または /en/ が含まれる場合
    if (/\/(ja|en)\//.test(path)) {
        return path.replace(/\/(ja|en)\//, `/${lang}/`);
    }
    // ③ 言語コードがどこにも無い場合は先頭に追加
    return `/${lang}${path}`;
};

/**
 * 診療科パスの取得
 */
export const getDepartmentPath = () => {
    const path = location.pathname;

    // /en/xxx or /ja/xxx
    const langMatch = path.match(/^\/(ja|en)\/([^/]+)/);
    if (langMatch) {
        return `/${langMatch[1]}/${langMatch[2]}`;
    }

    // /xxx
    const simpleMatch = path.match(/^\/([^/]+)/);
    return simpleMatch ? `/${simpleMatch[1]}` : "/";
};

/**
 * 言語リストの取得
 */
export const getListLanguage = () => [
    { text: 'EN', title: 'View this case in English', value: 'en' },
    { text: 'JA', title: 'この症例を日本語で見る', value: 'ja' }
];
